// Third Party
import React from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import classNames from 'classnames';
import throttle from 'lodash/throttle';

// Components
import HoldingsEditorHeader from './components/HoldingsEditorHeader';
import { HoldingIcon } from '../common';
import { PrimaryButton, SecondaryLink, ArrowNavigation, CancelButton, TertiaryButton, IconButton, MenuToggle } from '../buttons';
import { UnitInput, EditableInput } from '../inputs';
import { WarningTooltip, ErrorTooltip, Tooltip, InfoTooltip } from '../tooltips';
import { AllocationModeToggle, Pastille, CurrencyLabel, ContactPastille, PillLabel } from '../misc';

// Enums
import { HoldingAllocationMode } from '@/enums/Portfolio';

// Utils
import { formatMessage, FormattedMessage, formatLocalizedString } from '../../../utils/i18n';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '../../../utils/permissions';
import { HOLDINGS_EDITOR_TYPE, COMPARISON_ITEM_TYPES } from '../../../utils/constants';
import PortfolioHoldingPricesDisclaimer from '../disclaimers/PortfolioHoldingPricesDisclaimer';

// Types
import type { EditablePortfolioFields } from '../types/portfolios';
import Dictionary from '@/types/dictionary';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import PortfolioRiskProfile from '@/types/portfolio-risk-profile';
import Holding from '@/types/holding';
import PortfolioHolding from '@/types/portfolio-holding';
import BenchmarkHolding from '@/types/benchmark-holding';
import ThemeMode from '@/types/theme-mode';
import GenericContact from '@/types/generic-contact';
import { Dropdown } from 'react-bootstrap';

type OptionalProps = {
  className?: string;
  color?: string;
  readOnly?: boolean;
  dark?: boolean;
  theme?: ThemeMode;
  user?: Nullable<User>;
  saveIsDisabled?: boolean;
  saveLabel?: string;
  showDollars?: boolean;
  showMER?: boolean;
  contactName?: string;
  showAssetDetails?: boolean;
  splitCashAndFixedIncome?: boolean;
  withCharts?: boolean;
  customDisclaimer?: JSX.Element;
  size?: number;
  currency?: string;
  discretionaryMer?: number;
  riskProfile?: PortfolioRiskProfile;
  isPinned?: boolean;
  holdingsData?: Dictionary<Holding>;
  hasMissingHoldings?: boolean;
  editableFields?: EditablePortfolioFields;
  hasFileUploading?: boolean;
  contact?: GenericContact;
  canDeselect?: boolean;
  hasSearchInputOnTop?: boolean;
  riskProfileIsLocked?: boolean;
};

type FunctionProps = {
  setPortfolioName: (name: string) => void;
  onNameInputBlur: () => void;
  onSave: () => Promise<void>;
  onCancel?: () => void;
  onPortfolioSizeChange: (size: Nullable<number>) => void;
  onBlurPortfolioSizeChange: () => void;
  onFeeBPSChange: (mer: Nullable<number>) => void;
  onAllocateRemaining: () => void;
  onAllocateEvenly: () => void;
  renderCharts: () => Nullable<JSX.Element>;
  onUnpin?: () => void;
  onPin?: () => void;
  onAssignRiskProfile?: () => void;
  setRiskProfileForEdit?: (profile: PortfolioRiskProfile) => void;
  deletePortfolioRiskProfile?: () => void;
  onChangeAllocationMode: (mode: HoldingAllocationMode) => void;
};

type Props = OptionalProps &
  FunctionProps & {
    type: HOLDINGS_EDITOR_TYPE;
    name: string;
    holdings: (PortfolioHolding | BenchmarkHolding)[];
    quickSearchComponent?: JSX.Element;
    holdingsComponent: JSX.Element[];
    totalAllocated: number;
    allocationMode: HoldingAllocationMode;
    allocationModes: HoldingAllocationMode[];
  };

type State = {
  activeSearch: boolean;
  upArrowDisabled: boolean;
  downArrowDisabled: boolean;
};

const VERTICAL_SCROLL_DISTANCE = 100;

class HoldingsEditor extends React.Component<Props, State> {
  holdingsRef = React.createRef<HTMLDivElement>();

  scrollBarRef: HTMLElement | undefined;

  static defaultProps = {
    allocationMode: HoldingAllocationMode.Weight,
    setPortfolioName: () => {},
    onNameInputBlur: () => {},
    onSave: () => {},
    onCancel: () => {},
    onPortfolioSizeChange: () => {},
    onBlurPortfolioSizeChange: () => {},
    onFeeBPSChange: () => {},
    onAllocateRemaining: () => {},
    onAllocateEvenly: () => {},
    renderCharts: () => {},
    onChangeAllocationMode: () => {},
    allocationModes: [HoldingAllocationMode.Weight],
  };

  readonly state: State = {
    activeSearch: this.props.holdings.length === 0,
    upArrowDisabled: true,
    downArrowDisabled: true,
  };

  componentDidMount() {
    this.portfolioHoldingsOverflow();
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.holdings.length !== prevProps.holdings.length) {
      this.handleAutoScroll(prevProps.holdings);
      this.portfolioHoldingsOverflow();
    }
  }

  handleAutoScroll = (prevHoldings: (PortfolioHolding | BenchmarkHolding)[]) => {
    const isBenchmarkEditor = this.props.type === HOLDINGS_EDITOR_TYPE.BENCHMARK;
    const isHoldingAdded = this.props.holdings.length >= prevHoldings.length;
    const shouldScroll = isBenchmarkEditor && isHoldingAdded && this.holdingsExceedsScrollbar();

    if (!shouldScroll) {
      return;
    }

    if (this.scrollBarRef) {
      const { scrollHeight, clientHeight } = this.scrollBarRef;
      this.scrollBarRef.scrollTop = scrollHeight - clientHeight;
    }
  };

  onScrollUp = throttle(() => {
    if (this.state.downArrowDisabled) {
      this.setState({ downArrowDisabled: false });
    }
  }, 100);

  onScrollDown = throttle(() => {
    if (this.state.upArrowDisabled) {
      this.setState({ upArrowDisabled: false });
    }
  }, 100);

  onYReachStart = () => {
    if (!this.state.upArrowDisabled) {
      this.setState({ upArrowDisabled: true });
    }
  };

  onYReachEnd = () => {
    if (!this.state.downArrowDisabled) {
      this.setState({ downArrowDisabled: true });
    }
  };

  toggleAddFund = () => {
    this.setState(state => ({
      activeSearch: !state.activeSearch,
    }));
  };

  renderHoldings = () => {
    const { allocationMode, holdings, holdingsComponent, readOnly, showAssetDetails, showDollars, splitCashAndFixedIncome, type } = this.props;

    if (holdingsComponent.length > 0) {
      return (
        <PerfectScrollbar
          className="holdings-container"
          onYReachStart={this.onYReachStart}
          onYReachEnd={this.onYReachEnd}
          onScrollDown={this.onScrollDown}
          onScrollUp={this.onScrollUp}
          containerRef={ref => {
            this.scrollBarRef = ref;
          }}
        >
          {showAssetDetails && holdings.length > 0 && (
            <HoldingsEditorHeader
              allocationMode={allocationMode}
              className="mobile-only"
              showDollars={showDollars}
              splitCashAndFixedIncome={splitCashAndFixedIncome}
              readOnly={readOnly}
            />
          )}
          <div className="holdings" ref={this.holdingsRef}>
            {holdingsComponent}
          </div>
        </PerfectScrollbar>
      );
    }

    return (
      <div className="holdings-editor-no-funds d-flex align-items-center justify-content-center">
        <FormattedMessage id={`HoldingsEditor.EmptyHoldings.${type}`} />
      </div>
    );
  };

  renderSymbols = () => {
    const { holdings, type } = this.props;

    if (!holdings.length) {
      return <FormattedMessage id={`HoldingsEditor.Empty.${type}`} />;
    }

    return (
      <span>
        {holdings
          .map(i => i.symbol)
          .filter(v => v)
          .join(', ')}
      </span>
    );
  };

  holdingsExceedsScrollbar = () => {
    if (!this.scrollBarRef || !this.holdingsRef.current) {
      return false;
    }

    return this.holdingsRef.current.offsetHeight > this.scrollBarRef.offsetHeight;
  };

  portfolioHoldingsOverflow = () => {
    if (!this.props.holdings.length) {
      this.setState({ downArrowDisabled: true, upArrowDisabled: true });
      return;
    }

    if (!this.holdingsExceedsScrollbar()) {
      this.setState({ downArrowDisabled: true, upArrowDisabled: true });
    } else {
      this.setState({ downArrowDisabled: false });
    }
  };

  onArrowScroll = (distance: number) => {
    const scrollbarContainer = document.getElementsByClassName('scrollbar-container holdings-container ps')[0] as HTMLDivElement;
    if (!scrollbarContainer) {
      return;
    }
    scrollbarContainer.scrollBy({ top: distance });
  };

  showAllocateRemaining = () => {
    const { type, size, totalAllocated } = this.props;

    // If portfolio has anything between 0% and 100% allocated, offer to allocate remaining.
    switch (type) {
      case HOLDINGS_EDITOR_TYPE.PORTFOLIO: {
        return !!size && totalAllocated > 0 && totalAllocated < 100;
      }
      case HOLDINGS_EDITOR_TYPE.BENCHMARK: {
        return totalAllocated > 0 && totalAllocated < 100;
      }
      default:
        return false;
    }
  };

  showReallocateEvenly = () => {
    const { type, holdings, totalAllocated, size } = this.props;

    const hasEmptyHolding = holdings.some(h => !h.weight);

    // If portfolio has 0% or 100% (or greater) allocated offer to allocate (or reallocate) evenly.
    switch (type) {
      case HOLDINGS_EDITOR_TYPE.PORTFOLIO: {
        return !!size && ((hasEmptyHolding && (totalAllocated === 0 || totalAllocated === 100)) || totalAllocated > 100);
      }
      case HOLDINGS_EDITOR_TYPE.BENCHMARK: {
        return (hasEmptyHolding && (totalAllocated === 0 || totalAllocated === 100)) || totalAllocated > 100;
      }
      default:
        return false;
    }
  };

  onPortfolioSizeChange = (val: string) => {
    let size: Nullable<number> = null;
    if (val.trim()) {
      size = Number(val);
    }

    this.props.onPortfolioSizeChange(size);
  };

  onFeeBPSChange = (val: string) => {
    let mer: Nullable<number> = null;
    if (val.trim()) {
      mer = Number(val);
    }

    this.props.onFeeBPSChange(mer);
  };

  editPortfolioRiskProfile = () => {
    const { setRiskProfileForEdit, riskProfile } = this.props;
    if (setRiskProfileForEdit !== undefined && riskProfile !== undefined) {
      setRiskProfileForEdit(riskProfile);
    }
  };

  getRiskProfileActions = () => {
    const { riskProfile, readOnly: editorReadOnly } = this.props;

    if (editorReadOnly || (riskProfile != null && riskProfile.profile.isReadOnly)) {
      return undefined;
    }

    return [
      <IconButton
        key="edit"
        icon="pencil"
        noBackground
        tooltip={formatMessage('HoldingsEditor.EditRisk')}
        tooltipEffect="solid"
        onClick={this.editPortfolioRiskProfile}
      />,
      <IconButton
        key="trash"
        icon="ds-delete"
        noBackground
        tooltip={formatMessage('Common.Remove')}
        tooltipEffect="solid"
        onClick={this.props.deletePortfolioRiskProfile}
      />,
    ];
  };

  renderAllocationWarning = () => {
    const { totalAllocated, type } = this.props;
    if (totalAllocated > 100) {
      return <WarningTooltip message={formatMessage(`HoldingsEditor.OverAllocation.${type}`)} />;
    }

    if (totalAllocated < 100) {
      return <WarningTooltip message={formatMessage(`HoldingsEditor.UnderAllocation.${type}`)} />;
    }

    return null;
  };

  renderAllocateRemaining = () => {
    const { dark, totalAllocated, allocationMode } = this.props;

    if (allocationMode !== HoldingAllocationMode.Weight) {
      return null;
    }

    return (
      <div className="d-flex allocate-remaining-container">
        <div className="allocate-remaining flex-grow-1">
          {this.showAllocateRemaining() && (
            <SecondaryLink dark={dark} onClick={this.props.onAllocateRemaining}>
              <FormattedMessage id="HoldingsEditor.AllocateRemaining" />
            </SecondaryLink>
          )}
          {this.showReallocateEvenly() && (
            <SecondaryLink dark={dark} onClick={this.props.onAllocateEvenly}>
              <FormattedMessage id={`HoldingsEditor.${totalAllocated === 100 ? 'ReallocateEvenly' : 'AllocateEvenly'}`} />
            </SecondaryLink>
          )}
        </div>

        <FormattedMessage id="HoldingsEditor.TotalAllocated" />

        <span className="holdings-editor-total-allocated-value">{`${totalAllocated}%`}</span>

        {this.renderAllocationWarning()}
      </div>
    );
  };

  getPricingDatesDisclaimer(): React.ReactNode {
    const { allocationMode, holdings, holdingsData } = this.props;
    if (allocationMode !== HoldingAllocationMode.Units || !holdingsData) {
      return null;
    }

    return <PortfolioHoldingPricesDisclaimer holdings={holdings as PortfolioHolding[]} holdingsData={holdingsData} />;
  }

  renderUnitsWarning = (): React.ReactNode => {
    const { holdings, allocationMode } = this.props;

    if (allocationMode !== HoldingAllocationMode.Units) {
      return null;
    }

    let tooltip = null;
    if (holdings.some(holding => !holding.weight)) {
      tooltip = <WarningTooltip message={formatMessage('HoldingsEditor.MissingInformation')} />;
    }

    return (
      <div className="units-disclaimer">
        {this.getPricingDatesDisclaimer()}
        {tooltip}
      </div>
    );
  };

  getMenuItems = () => [
    {
      value: formatMessage('Common.Edit'),
      icon: 'icon-pencil',
      onClick: this.editPortfolioRiskProfile,
    },
    {
      value: formatMessage('Common.Remove'),
      icon: 'icon-remove-new',
      className: 'destructive',
      onClick: this.props.deletePortfolioRiskProfile,
    },
  ];

  renderOptions = () => {
    const { riskProfile, readOnly: editorReadOnly } = this.props;

    if (editorReadOnly || (riskProfile != null && riskProfile.profile.isReadOnly)) {
      return [];
    }

    return this.getMenuItems().map(item => (
      <Dropdown.Item key={item.value} role="menuitem" as="button" className={classNames(item.className)} onClick={() => item.onClick && item.onClick()}>
        <i className={item.icon} />
        <span>{item.value}</span>
      </Dropdown.Item>
    ));
  };

  render() {
    const {
      dark,
      theme,
      readOnly,
      user,
      color,
      saveIsDisabled,
      saveLabel,
      showDollars,
      showMER,
      contactName,
      showAssetDetails,
      splitCashAndFixedIncome,
      className,
      quickSearchComponent,
      withCharts,
      customDisclaimer,
      type,
      discretionaryMer,
      size,
      name,
      currency,
      holdings,
      onAssignRiskProfile,
      riskProfile,
      isPinned,
      onUnpin,
      onPin,
      allocationModes,
      allocationMode,
      hasMissingHoldings,
      editableFields,
      hasFileUploading,
      contact,
      canDeselect,
      riskProfileIsLocked,
    } = this.props;

    const { downArrowDisabled, upArrowDisabled, activeSearch } = this.state;

    const showCurrencies = !!user && userHasPermissionEnabled(user, PERMISSIONS.CURRENCY_CONVERSIONS);
    const showAssignRiskProfile = !!user && userHasPermissionEnabled(user, PERMISSIONS.RISK_QUESTIONNAIRE, PERMISSIONS.RISK_PROFILE) && !isPinned;
    const classes = classNames('holdings-editor', className, {
      dark,
      'theme-light': theme && theme === ThemeMode.LIGHT,
      'theme-default': theme && theme === ThemeMode.DEFAULT,
      'read-only': readOnly,
      'has-missing-holding': hasMissingHoldings,
    });

    const showBodyHeader =
      showDollars ||
      (showMER && user && userHasPermissionEnabled(user, PERMISSIONS.DISCRETIONARY_MER)) ||
      contactName ||
      showAssignRiskProfile ||
      (showAssetDetails && holdings.length > 0);

    const hasEditableFields = editableFields && Object.values(editableFields).some(item => item === true);

    const riskProfileIsEmpty = !riskProfile;
    const enableThemeOptions = theme && (theme === ThemeMode.DEFAULT || theme === ThemeMode.LIGHT);

    const searchInputComponent = (
      <>
        {!readOnly && quickSearchComponent !== undefined && (
          <div className="holdings-editor-add-funds">
            {activeSearch ? (
              quickSearchComponent
            ) : (
              <TertiaryButton dark={dark} invert onClick={this.toggleAddFund}>
                <i className="icon-plus" />
                <FormattedMessage id={`HoldingsEditor.AddHolding.${type}`} values={{ count: holdings.length }} />
              </TertiaryButton>
            )}
          </div>
        )}
      </>
    );

    return (
      <div className={classes} onClick={e => e.stopPropagation()}>
        <div className="holdings-editor-header">
          <div className="top-row d-flex">
            <div className="holdings-editor-infos flex-grow-1">
              {!enableThemeOptions && (
                <div className="holdings-editor-symbols">
                  <HoldingIcon type={COMPARISON_ITEM_TYPES.PORTFOLIO} color={color} />
                  <div className="symbols">{this.renderSymbols()}</div>
                </div>
              )}

              <div className="holdings-editor-name d-flex align-items-center">
                {enableThemeOptions && <HoldingIcon type={COMPARISON_ITEM_TYPES.PORTFOLIO} color={color} />}

                {!name && <ErrorTooltip message={formatMessage(`HoldingsEditor.NameDisclaimer.${type}`)} />}
                <EditableInput
                  compact
                  data-private
                  data-dd-privacy="mask"
                  dark={dark}
                  value={name}
                  placeholder={formatMessage(`HoldingsEditor.NamePlaceholder.${type}`)}
                  onValueChange={this.props.setPortfolioName}
                  defaultToEdit={!name}
                  onBlur={this.props.onNameInputBlur}
                  readOnly={readOnly && !editableFields?.name}
                />

                {currency && showCurrencies && (
                  <Tooltip message={formatMessage('HoldingsEditor.BaseCurrencyInfo')}>
                    <CurrencyLabel currency={currency} />
                  </Tooltip>
                )}

                {readOnly && !hasEditableFields && (
                  <Pastille className="light">
                    <FormattedMessage id="Common.ReadOnly" />
                  </Pastille>
                )}
              </div>
            </div>

            <div className="holdings-editor-actions d-flex">
              {onUnpin && (
                <div className="unpin-btn">
                  <TertiaryButton icon="expand-top-left" onClick={onUnpin}>
                    <FormattedMessage id="EditPortfolioModal.AdvancedEditor" />
                  </TertiaryButton>
                </div>
              )}

              {enableThemeOptions && contact && contactName && (
                <div className="contact d-flex flex-fill align-items-center">
                  <ContactPastille contact={contact} backgroundColor="#f2f2f2" borderColor="#e5e5e5" />
                  <span data-private data-dd-privacy="mask">
                    {contactName}
                  </span>
                </div>
              )}

              {this.props.onCancel && (
                <CancelButton
                  icon={saveIsDisabled && canDeselect ? 'minus-med' : ''}
                  label={saveIsDisabled && canDeselect ? 'Common.Deselect' : ''}
                  dark={dark}
                  onClick={this.props.onCancel}
                />
              )}

              {(!readOnly || (readOnly && hasEditableFields)) && (
                <PrimaryButton className="save" disabled={saveIsDisabled} onClick={this.props.onSave}>
                  {saveLabel && <span>{saveLabel}</span>}

                  {!saveLabel && <FormattedMessage id="HoldingsEditor.Save" />}
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>

        {this.props.hasSearchInputOnTop && searchInputComponent}

        <div
          className={classNames('holdings-editor-body d-flex flex-column position-relative', {
            'has-holdings-header': showAssetDetails && holdings.length > 0,
          })}
        >
          {hasMissingHoldings && <div className="missing-product-overlay" />}
          {showBodyHeader && (
            <div className={classNames('holdings-editor-body-header', { 'is-read-only': readOnly })}>
              <div className="bottom-row d-flex align-items-center holdings-editor-size-and-mer">
                {showDollars && (
                  <div className="portfolio-input-wrapper portfolio-size-input align-items-center">
                    <label htmlFor="portfolio-size">
                      <FormattedMessage id="HoldingsEditor.Size" />
                    </label>

                    <UnitInput
                      id="portfolio-size"
                      name="portfolio-size"
                      compact
                      dark={dark}
                      theme={theme}
                      value={size != null ? size : ''}
                      type="number"
                      min={0}
                      onValueChange={this.onPortfolioSizeChange}
                      onBlur={this.props.onBlurPortfolioSizeChange}
                      unitSymbol="$"
                      disabled={readOnly && !editableFields?.size}
                      controlled={false}
                    />
                  </div>
                )}

                {showMER && user && userHasPermissionEnabled(user, PERMISSIONS.DISCRETIONARY_MER) && (
                  <div className="portfolio-input-wrapper portfolio-mer-input align-items-center">
                    <label htmlFor="portfolio-mer">
                      <FormattedMessage id="HoldingsEditor.DiscretionaryMER" />
                      <InfoTooltip message={formatMessage('HoldingsEditor.DiscretionaryMERInfo')} />
                    </label>

                    <UnitInput
                      id="portfolio-mer"
                      name="portfolio-mer"
                      compact
                      dark={dark}
                      theme={theme}
                      value={discretionaryMer != null ? Number((discretionaryMer * 100).toFixed(4)) : ''}
                      type="number"
                      step={1}
                      onValueChange={this.onFeeBPSChange}
                      unitSymbol={formatMessage('HoldingsEditor.DiscretionaryMERUnit')}
                      disabled={readOnly && !editableFields?.mer}
                    />
                  </div>
                )}
              </div>

              {(contactName || showAssignRiskProfile) && (
                <div className="holdings-editor-contact-and-risk d-flex align-items-center flex-grow-1">
                  <div
                    className={classNames('contact-and-risk-wrapper d-flex align-items-center', {
                      'is-empty': riskProfileIsEmpty,
                      'not-asset-details': !showAssetDetails,
                    })}
                  >
                    {!enableThemeOptions && contactName && (
                      <div className="d-flex flex-fill">
                        <i className="icon-user" />
                        <FormattedMessage id="HoldingsEditor.PortfolioFor" />
                        <span className="portfolio-contact-name" data-private data-dd-privacy="mask">
                          {contactName}
                        </span>
                      </div>
                    )}

                    {!enableThemeOptions && !readOnly && showAssignRiskProfile && riskProfile != null && (
                      <>
                        <FormattedMessage id="HoldingsEditor.RiskProfile" />
                        <PillLabel withTooltip text={formatLocalizedString(riskProfile.profile.title)} customElements={this.getRiskProfileActions()} />
                      </>
                    )}

                    {enableThemeOptions && !readOnly && showAssignRiskProfile && riskProfile != null && (
                      <>
                        <strong className="risk-label">
                          <FormattedMessage id="HoldingsEditor.RiskProfile" />:
                        </strong>
                        <span className="risk-value">{formatLocalizedString(riskProfile.profile.title)}</span>
                        {riskProfileIsLocked ? (
                          <Tooltip message={formatMessage('PortfolioEditorBuilder.RiskProfile.RiskProfileLockedTooltip')}>
                            <i className="icon-lock" />
                          </Tooltip>
                        ) : (
                          <MenuToggle options={this.renderOptions()} alignRight ariaLabel={formatMessage('RiskProfile.MenuToggleSelected')} />
                        )}
                      </>
                    )}

                    {!enableThemeOptions && !readOnly && showAssignRiskProfile && riskProfile == null && onAssignRiskProfile && (
                      <>
                        <FormattedMessage id="HoldingsEditor.RiskProfile" />
                        <TertiaryButton className="assign-risk-profile-btn" onClick={onAssignRiskProfile} compact dark={dark}>
                          <FormattedMessage id="HoldingsEditor.AssignRiskProfile" />
                        </TertiaryButton>
                      </>
                    )}

                    {enableThemeOptions && !readOnly && showAssignRiskProfile && riskProfile == null && onAssignRiskProfile && (
                      <TertiaryButton className="add-risk-profile" onClick={onAssignRiskProfile}>
                        <i className="icon-plus-med" />
                        <FormattedMessage id={`RiskProfile.AddRiskProfile${type === HOLDINGS_EDITOR_TYPE.PORTFOLIO ? 'ToPortfolio' : ''}`} />
                      </TertiaryButton>
                    )}

                    {readOnly && showAssignRiskProfile && riskProfile != null && (
                      <>
                        <strong className="risk-label">
                          <FormattedMessage id="HoldingsEditor.RiskProfile" />:
                        </strong>
                        <span className="risk-value">{formatLocalizedString(riskProfile.profile.title)}</span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {allocationModes.length > 1 && holdings.length > 0 && (
                <div className="allocation-mode-container d-flex justify-content-end">
                  <AllocationModeToggle
                    className="allocation-mode-toggle blue-selection"
                    dark={dark}
                    disabled={readOnly}
                    onChange={this.props.onChangeAllocationMode}
                    value={allocationMode}
                  />
                </div>
              )}

              {showAssetDetails && holdings.length > 0 && (
                <HoldingsEditorHeader
                  allocationMode={allocationMode}
                  showDollars={showDollars}
                  splitCashAndFixedIncome={splitCashAndFixedIncome}
                  readOnly={readOnly}
                />
              )}
            </div>
          )}

          <div className="holdings-editor-holdings-container" aria-live="polite">
            <span className="visually-hidden">{`${holdings.length} ${formatMessage('Common.Accessibility.ItemsInBenchmark')}`}</span>
            <ArrowNavigation
              direction="up"
              className={classNames({ disabled: upArrowDisabled })}
              onClick={() => this.onArrowScroll(-VERTICAL_SCROLL_DISTANCE)}
            />
            {this.renderHoldings()}
            <ArrowNavigation
              direction="down"
              className={classNames({ disabled: downArrowDisabled })}
              onClick={() => this.onArrowScroll(VERTICAL_SCROLL_DISTANCE)}
            />
          </div>

          {!this.props.hasSearchInputOnTop && searchInputComponent}

          <div className="holdings-editor-disclaimers d-flex align-items-center">
            {customDisclaimer}
            {readOnly && onPin && !hasFileUploading && userHasPermission(user, PERMISSIONS.PORTFOLIO_EDITOR_PINNED) && (
              <div className="quick-add-footer">
                <TertiaryButton icon="expand-bottom-right" onClick={onPin}>
                  <FormattedMessage id="EditPortfolioModal.PinnedEditor" />
                </TertiaryButton>
              </div>
            )}
            <div className={classNames('holdings-editor-allocation', { 'full-width': !showAssetDetails })}>
              {!readOnly && (
                <>
                  {this.renderAllocateRemaining()}
                  {this.renderUnitsWarning()}
                </>
              )}
              {withCharts && <div className="charts d-flex align-items-center">{this.props.renderCharts()}</div>}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default HoldingsEditor;
